import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'

import breadcrumbSchema, { contentfulSchema, gameSchema } from '../../../services/jsonld/jsonldSchema'
import PageTemplate from '../../templates/PageTemplate'
import Footer from '../../../containers/Footer/index'
import { venture, baseUrl, homePageUrl } from '../../../config'
import ComponentSelector from '../../ComponentSelector'
import template from './componentTemplate'
import { getDefaultMetaDescription } from '../../../utils'
import { handleGameTemplate } from './constants'

const DynamicHeader = loadable(() =>
  import('@albatross/common-components/dist/components/DynamicHeader')
)
const HTMLHeaderElements = loadable(() => import('@albatross/common-components/dist/components/MetaTags'))

const GamePage = (props) => {
  const {
    game,
    globalContent,
    globalComponents,
    ConfigContxt,
    addGameLegalLine,
    resetGamesLegalLines,
    enableContentful,
    path,
    data
  } = props

  const { provider } = data?.contentfulPageOrGame?.game ?? {}

  const categories = enableContentful
    ? [
        {
          slug: game?.categoryId,
          name: game?.categoryName || game?.categoryId
        },
        {
          slug: game?.subCategoryId,
          name: game?.subCategoryName || game?.subCategoryId
        }
      ]
    : globalContent.categories
  const url = `${baseUrl}${homePageUrl}${game.categoryId}/${game.id}`
  const schemaValues = { url, base: baseUrl, gameTitle: game.title, categories }
  const gameJsonLd = game?.indexed ? gameSchema(game.title, game.categoryId, game.introduction) : ''
  const contentfulJsonLd = contentfulSchema(game.jsonld)
  const jsonldArray = enableContentful ? contentfulJsonLd : [gameJsonLd, breadcrumbSchema(schemaValues)].filter(x => x)

  useEffect(() => {
    resetGamesLegalLines()
    if (game.providerId) {
      addGameLegalLine(game.providerId)
    }
  }, [game.providerId])
  const categoryName = globalContent?.categories?.find(
    (category) => category.slug === game?.categoryId
  )?.name

  const getComponents = () => {
    const gamePageTemplate = globalContent?.templates?.find(
      (template) => template?.name === 'gameTemplate'
    )

    const components = template(
      game.categoryId,
      categoryName,
      gamePageTemplate
    )
    return components
  }

  const handleGameComponents = () => {
    if (enableContentful) {
      let components = handleGameTemplate(venture)
      const gameCarouselIndex = components.findIndex(component => component.type === 'GameCarousel' || component.type === 'GamesRow')

      if (gameCarouselIndex !== -1) {
        const gameCrossSellQuery = game?.crossSellQuery || globalContent?.gameCrossSellQuery

        if (gameCrossSellQuery) {
          components[gameCarouselIndex].props.query = gameCrossSellQuery
        } else {
          // remove SubTitle and GameCarousel/GamesRow component
          components.splice(gameCarouselIndex - 1, 2)
        }
      }

      if (game?.customRegistrationUrl) {
        components = components.map((c) =>
          c.type === 'CallToAction'
            ? { ...c, props: { ...c.props, href: game?.customRegistrationUrl } }
            : c
        )
      }

      return template(game.categoryId, game.categoryId, components)
    } else {
      return getComponents()
    }
  }

  const components = handleGameComponents()

  const hasSubNav = !!components.filter(component => component.type === 'NavigationMenu' || component.type === 'UnicornNavigationMenu').length
  const gameCategoryId = game.categoryId
  const gameCategory = gameCategoryId ? globalContent?.categories?.find((category) => category.slug === gameCategoryId) : undefined

  return (
    <PageTemplate
      components={components}
      globalContent={globalContent}
    >
      <HTMLHeaderElements
        canonical={game.canonical || url}
        title={game.metaTitle}
        metaTags={[
          {
            name: 'description',
            content:
              game.metaDescription ||
              getDefaultMetaDescription(game.introduction)
          }
        ]}
        jsonld={jsonldArray}
        headerLang={globalContent.headerLang}
        currentPathname={path}
        indexed={game.indexed}
      />
        <DynamicHeader
          venture={venture}
          gameCategory={gameCategory}
          customRegistrationUrl={game?.customRegistrationUrl}
          globalConfig={globalComponents?.components?.DynamicHeader?.globalConfig}
          pageConfig={game?.overrideComponents?.header}
          categories={globalContent.menuItems}
          hasSubNav={hasSubNav}
          appLink={globalContent.appLinks}
          enableContentful={enableContentful}
        />
      <ComponentSelector
        {...{ components, game, globalContent, globalComponents, ConfigContxt, enableContentful }}
      />
      <Footer
        venture={venture}
        pageFooterOverride={game.pageFooterOverride}
        enableContentful={enableContentful}
        provider={provider}
      />
    </PageTemplate>
  )
}

GamePage.propTypes = {
  game: PropTypes.object,
  globalContent: PropTypes.object,
  globalComponents: PropTypes.object,
  ConfigContxt: PropTypes.object,
  addGameLegalLine: PropTypes.func,
  resetGamesLegalLines: PropTypes.func,
  enableContentful: PropTypes.bool,
  path: PropTypes.string,
  data: PropTypes.object
}

export default GamePage
